/**
 * Generate a branded EnayaHub booking-receipt PDF for a single appointment.
 *
 * Uses jsPDF with no external assets — the logo is embedded as a tiny inline
 * data-URI so the PDF works fully offline and prints identically across
 * browsers (Chrome/Safari/Firefox).
 */
import { jsPDF } from 'jspdf';

// --- Brand tokens (kept in sync with the in-app header look & feel) ---
const BRAND = {
  primary: [37, 99, 235],        // tailwind blue-600 — header band
  primaryDark: [29, 78, 216],    // tailwind blue-700 — divider
  accent: [16, 185, 129],        // tailwind emerald-500 — reference badge
  ink: [17, 24, 39],             // gray-900
  muted: [107, 114, 128],        // gray-500
  hairline: [229, 231, 235],     // gray-200
};

// Heart SVG (matches the app header icon) drawn programmatically — keeps file
// size tiny and avoids needing to ship a logo image with the bundle.
function drawHeartLogo(doc, x, y, size = 14) {
  // Card background
  doc.setFillColor(...BRAND.primary);
  doc.roundedRect(x, y, size, size, 3, 3, 'F');

  // Heart inside the card (white)
  doc.setFillColor(255, 255, 255);
  const cx = x + size / 2;
  const cy = y + size / 2 + 0.5;
  const w = size * 0.55;
  const h = size * 0.5;
  // Two circles + triangle to make a heart shape
  doc.circle(cx - w / 4, cy - h / 6, w / 4, 'F');
  doc.circle(cx + w / 4, cy - h / 6, w / 4, 'F');
  doc.triangle(
    cx - w / 2, cy - h / 12,
    cx + w / 2, cy - h / 12,
    cx,         cy + h / 2,
    'F',
  );
}

function fmtField(value) {
  if (value === null || value === undefined || value === '') return '—';
  if (typeof value === 'object') return value.name || '—';
  return String(value);
}

/**
 * Build and trigger download of the receipt PDF.
 * @param {object} appointment    The appointment object returned by the API.
 * @param {object} [opts]
 * @param {'download'|'print'} [opts.mode='download']  Open print dialog or save.
 */
export function generateBookingReceiptPdf(appointment, opts = {}) {
  const mode = opts.mode || 'download';
  if (!appointment) return;

  const doc = new jsPDF({ unit: 'mm', format: 'a4' });
  const pageW = doc.internal.pageSize.getWidth();

  // ── HEADER BAND (branded) ──────────────────────────────────────────────
  doc.setFillColor(...BRAND.primary);
  doc.rect(0, 0, pageW, 36, 'F');

  drawHeartLogo(doc, 14, 11, 14);

  doc.setTextColor(255, 255, 255);
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(20);
  doc.text('EnayaHub', 32, 18);

  doc.setFont('helvetica', 'normal');
  doc.setFontSize(10);
  doc.text('Healthcare Excellence', 32, 24);

  // Receipt label on the right
  doc.setFontSize(11);
  doc.text('BOOKING RECEIPT', pageW - 14, 18, { align: 'right' });
  doc.setFontSize(9);
  const issued = new Date().toLocaleString('en-GB', {
    day: '2-digit', month: 'short', year: 'numeric',
    hour: '2-digit', minute: '2-digit',
  });
  doc.text(`Issued: ${issued}`, pageW - 14, 24, { align: 'right' });

  // ── REFERENCE BADGE ────────────────────────────────────────────────────
  const ref = appointment.reference_number || appointment.confirmation_number || '—';
  doc.setFillColor(...BRAND.accent);
  doc.roundedRect(14, 46, pageW - 28, 16, 2, 2, 'F');
  doc.setTextColor(255, 255, 255);
  doc.setFont('helvetica', 'normal');
  doc.setFontSize(9);
  doc.text('REFERENCE NUMBER', 18, 52);
  doc.setFont('courier', 'bold');
  doc.setFontSize(15);
  doc.text(ref, 18, 59);

  doc.setFont('helvetica', 'normal');
  doc.setFontSize(9);
  doc.text(
    'Quote this reference in any correspondence with EnayaHub support.',
    pageW - 18, 59, { align: 'right' },
  );

  // ── DETAILS TABLE ──────────────────────────────────────────────────────
  const rows = [
    ['Customer',  fmtField(appointment.patient_name)],
    ['Phone',     fmtField(appointment.patient_phone)],
    ['Email',     fmtField(appointment.patient_email)],
    ['Service',   fmtField(appointment.service_name)],
    ['Provider',  fmtField(appointment.doctor_name || appointment.clinic_name || appointment.provider_name)],
    ['Date',      fmtField(appointment.appointment_date)],
    ['Time',      fmtField(appointment.appointment_time)],
    ['Status',    fmtField(appointment.status)],
  ];

  const fee = Number(appointment.consultation_fee || 0);
  const addons = Array.isArray(appointment.addons) ? appointment.addons : [];
  const addonsTotal = Number(appointment.addons_total || 0);
  const pointsUsed = Number(appointment.points_redeemed || 0);
  const pointsDiscount = Number(appointment.points_discount || 0);
  const pointsEarned = Number(appointment.points_earned || 0);
  const paid = appointment.amount_payable !== undefined && appointment.amount_payable !== null
    ? Number(appointment.amount_payable)
    : fee;

  if (fee) {
    rows.push(['Service price', `${(fee - addonsTotal).toFixed(3)} KWD`]);
  }
  addons.forEach((addon) => {
    rows.push([`Extra: ${addon.name}`, `${Number(addon.price || 0).toFixed(3)} KWD`]);
  });
  if (pointsUsed) {
    rows.push(['Points used', `-${pointsDiscount.toFixed(3)} KWD  (${pointsUsed} points)`]);
  }
  if (fee) {
    rows.push(['Total paid', `${paid.toFixed(3)} KWD`]);
  }
  if (appointment.payment_status) {
    rows.push(['Payment', fmtField(appointment.payment_status)]);
  }

  let y = 76;
  doc.setTextColor(...BRAND.ink);
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(12);
  doc.text('Booking Details', 14, y);
  y += 4;
  doc.setDrawColor(...BRAND.primaryDark);
  doc.setLineWidth(0.4);
  doc.line(14, y, pageW - 14, y);
  y += 6;

  doc.setFontSize(10);
  rows.forEach(([label, value], idx) => {
    if (idx % 2 === 1) {
      doc.setFillColor(248, 250, 252); // gray-50 zebra
      doc.rect(14, y - 4.5, pageW - 28, 8, 'F');
    }
    doc.setFont('helvetica', 'bold');
    doc.setTextColor(...BRAND.muted);
    doc.text(label, 18, y);
    doc.setFont('helvetica', 'normal');
    doc.setTextColor(...BRAND.ink);
    // Wrap long values (e.g. emails) so they don't overflow.
    const wrapped = doc.splitTextToSize(String(value), pageW - 60);
    doc.text(wrapped, 60, y);
    y += Math.max(8, wrapped.length * 5);
  });

  // ── POINTS BOX ─────────────────────────────────────────────────────────
  if (pointsEarned || pointsUsed) {
    y += 4;
    doc.setFillColor(236, 253, 245);  // emerald-50
    doc.setDrawColor(110, 231, 183);  // emerald-300
    doc.setLineWidth(0.3);
    const lines = [];
    if (pointsEarned) {
      lines.push(`You earned ${pointsEarned} points on this booking - worth ${(pointsEarned / 100).toFixed(3)} KWD off a future one.`);
    }
    if (pointsUsed) {
      lines.push(`You used ${pointsUsed} points on this booking, saving ${pointsDiscount.toFixed(3)} KWD.`);
    }
    lines.push('Points are earned on online bookings only. 100 points = 1 KWD. They never expire.');
    const boxH = 10 + lines.length * 5;
    doc.roundedRect(14, y, pageW - 28, boxH, 2, 2, 'FD');
    doc.setTextColor(6, 95, 70); // emerald-800
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(10);
    doc.text('EnayaHub Points', 18, y + 6);
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(9);
    lines.forEach((line, i) => doc.text(line, 18, y + 12 + i * 5));
    y += boxH + 4;
  }

  // ── IMPORTANT INFO BOX ─────────────────────────────────────────────────
  y += 4;
  doc.setFillColor(255, 251, 235);    // amber-50
  doc.setDrawColor(253, 230, 138);    // amber-300
  doc.setLineWidth(0.3);
  const infoH = 28;
  doc.roundedRect(14, y, pageW - 28, infoH, 2, 2, 'FD');
  doc.setTextColor(120, 53, 15); // amber-900
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(10);
  doc.text('Important Information', 18, y + 6);
  doc.setFont('helvetica', 'normal');
  doc.setFontSize(9);
  const tips = [
    '• Please arrive 15 minutes before your scheduled time.',
    '• Bring a valid ID and any relevant medical documents.',
    '• To reschedule or cancel, use the magic link sent to your email / SMS.',
  ];
  tips.forEach((t, i) => doc.text(t, 18, y + 12 + i * 5));
  y += infoH + 8;

  // ── FOOTER ─────────────────────────────────────────────────────────────
  const footerY = doc.internal.pageSize.getHeight() - 18;
  doc.setDrawColor(...BRAND.hairline);
  doc.setLineWidth(0.3);
  doc.line(14, footerY - 4, pageW - 14, footerY - 4);

  doc.setTextColor(...BRAND.muted);
  doc.setFont('helvetica', 'normal');
  doc.setFontSize(8);
  doc.text('EnayaHub (عناية هاب) — Healthcare Excellence', 14, footerY);
  doc.text('support@enayahub.net   |   +965 6552 7758   |   enayahub.net',
    14, footerY + 4);
  doc.text(`© ${new Date().getFullYear()} EnayaHub. All rights reserved.`,
    pageW - 14, footerY + 4, { align: 'right' });

  const filename = `EnayaHub-Receipt-${ref}.pdf`;
  if (mode === 'print') {
    // Open the PDF in a new tab and trigger the browser print dialog.
    const blobUrl = doc.output('bloburl');
    const win = window.open(blobUrl, '_blank');
    if (win) {
      win.addEventListener('load', () => {
        try { win.print(); } catch (_) { /* noop */ }
      });
    }
  } else {
    doc.save(filename);
  }
}
